import React, { useEffect, useState, useRef } from "react";
import { Filter, Check, Trash2, Search } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  clearError,
  deleteMultipleUsers,
} from "../../store/slices/userSlice";
import { withVersion } from "../../utils/concurrency";
import { CreateUserData, User, UserFilters } from "../../types";
import Button from "../../components/UI/Button";
import { Modal } from "../../components/UI/Modal";
import { UserForm } from "../../components/users/UserForm";
import { UserTable } from "../../components/users/UserTable";
import { Pagination } from "../../components/ListView/Pagination";
import { ChevronDown } from "lucide-react";
import { UserFilters as Filters } from "../../components/users/UserFilters";
import { Input } from "../../components/UI/FormField";

export const UsersList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("users");

  const {
    users,
    loading,
    error,
    pagination: { total, page, pages },
  } = useAppSelector((state) => state.users);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<UserFilters>({
    search: "",
    page: 1,
    limit: 20,
    status: "",
    role: undefined,
  });

  const prevSearch = useRef(filters.search);

  useEffect(() => {
    const searchChanged = prevSearch.current !== filters.search;
    prevSearch.current = filters.search;

    const params = {
      search: filters.search,
      page: filters.page,
      limit: filters.limit,
      status: filters.status,
      role: filters.role,
    };

    if (searchChanged) {
      if (filters.search === "") {
        dispatch(getUsers(params));
      } else {
        const timer = setTimeout(() => dispatch(getUsers(params)), 750);
        return () => clearTimeout(timer);
      }
      return;
    }
    dispatch(getUsers(params));
  }, [dispatch, filters]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleFiltersChange = (newFilters: Partial<UserFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: newFilters.page || 1,
    }));
    setSelectedIds([]);
  };

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(users.map((user) => user._id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
    }
  };

  const handleCreateUser = async (data: CreateUserData) => {
    try {
      await dispatch(createUser(data)).unwrap();
      toast.success(t("usersList.toast.createSuccess"));
      setIsModalOpen(false);
    } catch (error) {}
  };

  const handleUpdateUser = async (data: Partial<User>) => {
    if (!editingUser) return;

    try {
      await dispatch(
        updateUser({
          id: editingUser._id,
          userData: withVersion(data, editingUser),
        }),
      ).unwrap();
      toast.success(t("usersList.toast.updateSuccess"));
      setIsModalOpen(false);
      setEditingUser(null);
    } catch (error) {
      if (typeof error === "string") toast.error(error);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t("usersList.confirm.delete"))) {
      try {
        await dispatch(deleteUser(id)).unwrap();
        toast.success(t("usersList.toast.deleteSuccess"));
        setSelectedIds((prev) =>
          prev.filter((selectedId) => selectedId !== id),
        );
      } catch (error) {}
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    if (
      window.confirm(
        t("usersList.confirm.bulkDelete", { count: selectedIds.length }),
      )
    ) {
      try {
        await dispatch(deleteMultipleUsers(selectedIds)).unwrap();
        toast.success(
          t("usersList.toast.bulkDeleteSuccess", { count: selectedIds.length }),
        );
        setSelectedIds([]);
      } catch (error) {}
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  const pagination = {
    page,
    pages,
    total,
    hasNext: page < pages,
    hasPrev: page > 1,
  };

  // Derive translated search placeholder based on current role filter
  const searchRoleKey =
    filters.role === "admin"
      ? "usersList.controls.searchRoles.admin"
      : filters.role === "driver"
        ? "usersList.controls.searchRoles.driver"
        : "usersList.controls.searchRoles.default";

  const roleOptions = [
    { value: "user", label: t("usersList.roleOptions.user") },
    { value: "admin", label: t("usersList.roleOptions.admin") },
  ];

  return (
    <div>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-dark-900 mb-4">
            {t("usersList.page.title")}
          </h1>

          <div className="flex items-center justify-between gap-4 max-sm:flex-wrap">
            <div className="flex-1 min-w-[250px] max-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder={t("usersList.controls.searchPlaceholder", {
                    role: t(searchRoleKey),
                  })}
                  value={filters.search}
                  onChange={(e) =>
                    handleFiltersChange({ search: e.target.value })
                  }
                  className="pl-10 bg-white"
                />
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="relative">
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter className="w-4 h-4" />
                  <span>{t("usersList.controls.filter")}</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
                {showFilters && (
                  <div className="absolute right-0 max-sm:left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-10">
                    <Filters
                      filters={filters}
                      setShowFilters={setShowFilters}
                      onFiltersChange={handleFiltersChange}
                      roleOptions={roleOptions}
                      onClearFilters={() =>
                        setFilters({
                          search: "",
                          page: 1,
                          limit: 20,
                          status: "",
                          role: undefined,
                        })
                      }
                    />
                  </div>
                )}
              </div>

              <Button onClick={() => setIsModalOpen(true)} variant="primary">
                {filters.role === "admin"
                  ? t("usersList.controls.addAdmin")
                  : t("usersList.controls.addUser")}
              </Button>
            </div>
          </div>
        </div>

        {selectedIds.length > 0 && (
          <div className="mb-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Check className="h-5 w-5 text-blue-600" />
              <span className="text-blue-800 font-medium">
                {t("selection.itemsSelected", {
                  count: selectedIds.length,
                  plural: selectedIds.length !== 1 ? "s" : "",
                })}
              </span>
            </div>
            <button
              onClick={handleBulkDelete}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              <span>{t("selection.deleteSelected")}</span>
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <UserTable
            users={users}
            selectedIds={selectedIds}
            onSelectAll={handleSelectAll}
            onSelectOne={handleSelectOne}
            onEdit={handleEdit}
            onDelete={handleDelete}
            loading={loading}
            isUsersPage={true}
          />
          <Pagination
            pagination={pagination}
            onPageChange={handlePageChange}
            pageSize={filters.limit}
          />
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={
          editingUser
            ? t("usersList.modal.editTitle", {
                id: editingUser._id.slice(-6).toUpperCase(),
              })
            : filters.role === "admin"
              ? t("usersList.modal.createAdminTitle")
              : t("usersList.modal.createUserTitle")
        }
        size="xl"
      >
        <div className="flex-1 overflow-y-auto">
          <UserForm
            user={editingUser || undefined}
            onSubmit={(data) => {
              if (editingUser) {
                return handleUpdateUser(data);
              } else {
                return handleCreateUser(data as CreateUserData);
              }
            }}
            loading={loading}
            isEditMode={!!editingUser}
            roleOptions={editingUser ? undefined : roleOptions}
          />
        </div>
      </Modal>
    </div>
  );
};
